/* global chrome */
import React, { createContext, useState, useRef, useEffect, useCallback } from 'react';
import { trackEvent, AnalyticsEvents } from '../utils/analytics';
import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';

export const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [error, setError] = useState(null); // <--- ADD THIS LINE for error state
  const [bufferedTime, setBufferedTime] = useState(0); // Add buffered time state

  const audioRef = useRef(new Audio());
  const startPositionRef = useRef(0);

  const togglePlayPause = useCallback(() => { // Defined FIRST
    if (!currentEpisode) return;
    const newIsPlaying = !isPlaying;
    setIsPlaying(newIsPlaying);
    
    trackEvent(newIsPlaying ? AnalyticsEvents.PLAY_START : AnalyticsEvents.PLAY_PAUSE, {
      episodeId: currentEpisode.id,
      episodeTitle: currentEpisode.title,
      podcastId: currentEpisode.podcastId,
      position: currentTime
    }, auth.currentUser?.uid);

    if (window.chrome && chrome.runtime) {
      try {
        chrome.runtime.sendMessage('efdnlegncogblfpgmhmaeibblfehpljl', {
          action: newIsPlaying ? "PLAY_PODCAST" : "PAUSE_PODCAST",
          streamUrl: currentEpisode.enclosureUrl,
          timestamp: currentTime,
          podcastTitle: currentEpisode.podcastTitle || "Listen2 Podcast",
          episodeTitle: currentEpisode.title
        });
      } catch (e) {
        console.warn("Could not communicate with extension", e);
      }
    }

    if (!newIsPlaying && audioRef.current && duration > 0) {
      const time = audioRef.current.currentTime;
      const percentage = time / duration;
      const storageKey = `listen2_last_played_${auth.currentUser?.uid || 'guest'}`;
      if (percentage >= 0.03 && percentage <= 0.98) {
        const state = {
          episode: currentEpisode,
          currentTime: time,
          duration: duration,
          timestamp: Date.now()
        };
        localStorage.setItem(storageKey, JSON.stringify(state));
        if (auth.currentUser) {
          setDoc(doc(db, 'userPlaybackState', auth.currentUser.uid), state, { merge: true }).catch(console.error);
        }
      } else if (percentage > 0.98) {
        localStorage.removeItem(storageKey);
      }
    }
  }, [currentEpisode, isPlaying, currentTime, duration]); // Added setIsPlaying for completeness, though it's stable

  const playEpisode = useCallback((episode, startTime = 0) => { // Defined SECOND, now it can access togglePlayPause
    if (!episode || !episode.enclosureUrl) {
      console.error("AudioContext: Cannot play episode: missing URL or episode object", episode);
      return;
    }

    startPositionRef.current = startTime;

    if (currentEpisode?.id !== episode.id) {
      setCurrentEpisode(episode);
      if (audioRef.current) {
          audioRef.current.src = episode.enclosureUrl;
          setIsLoadingMetadata(true); 
      }
      setIsPlaying(true); // Auto-play new episode
      
      trackEvent(AnalyticsEvents.PLAY_START, {
        episodeId: episode.id,
        episodeTitle: episode.title,
        podcastId: episode.podcastId,
        isNewSelection: true
      }, auth.currentUser?.uid);

      if (window.chrome && chrome.runtime) {
        try {
          chrome.runtime.sendMessage('efdnlegncogblfpgmhmaeibblfehpljl', {
            action: "PLAY_PODCAST",
            streamUrl: episode.enclosureUrl,
            timestamp: startTime,
            podcastTitle: episode.podcastTitle || "Listen2 Podcast",
            episodeTitle: episode.title
          });
        } catch (e) {
          console.warn("Could not communicate with extension", e);
        }
      }
    } else {
      // If it's the same episode, just toggle play/pause
      togglePlayPause();
    }
  }, [currentEpisode, togglePlayPause, audioRef, setIsLoadingMetadata, setCurrentEpisode, setIsPlaying]);

  useEffect(() => {
    if (!audioRef.current || !currentEpisode) return;

    if (isPlaying) {
      audioRef.current.play().catch(e => {
        console.error("Error playing audio:", e);
        setIsPlaying(false); // Reset if play fails
      });
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, currentEpisode, audioRef.current?.src]); // Depend on src as well for re-play on src change

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const saveProgress = (time, dur) => {
      if (!currentEpisode || dur <= 0) return;
      const percentage = time / dur;
      const storageKey = `listen2_last_played_${auth.currentUser?.uid || 'guest'}`;
      if (percentage >= 0.03 && percentage <= 0.98) {
        const state = {
          episode: currentEpisode,
          currentTime: time,
          duration: dur,
          timestamp: Date.now()
        };
        localStorage.setItem(storageKey, JSON.stringify(state));
        if (auth.currentUser) {
          setDoc(doc(db, 'userPlaybackState', auth.currentUser.uid), state, { merge: true }).catch(console.error);
        }
      } else if (percentage > 0.98) {
        localStorage.removeItem(storageKey);
      }
    };

    let lastSaveTime = 0;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoadingMetadata(false);
      if (startPositionRef.current > 0) {
        audio.currentTime = startPositionRef.current;
        setCurrentTime(startPositionRef.current);
        startPositionRef.current = 0;
      }
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      const now = Date.now();
      if (now - lastSaveTime > 5000) {
        saveProgress(audio.currentTime, audio.duration);
        lastSaveTime = now;
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      
      trackEvent(AnalyticsEvents.PLAY_COMPLETE, {
        episodeId: currentEpisode?.id,
        episodeTitle: currentEpisode?.title,
        podcastId: currentEpisode?.podcastId
      }, auth.currentUser?.uid);

      // Optional: Play next, clear episode, etc.
      console.log("Episode ended");
    };
    const handleVolumeChange = () => {
      setVolume(audio.volume);
    };
    const handleError = (e) => {
      console.error("Audio Element Error:", e);
      setIsPlaying(false);
      setCurrentEpisode(null); 
      setError("Error playing audio."); // This will now work
    };
    const handleProgress = () => {
      if (audio.buffered.length > 0) {
        setBufferedTime(audio.buffered.end(audio.buffered.length - 1));
      } else {
        setBufferedTime(0);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('volumechange', handleVolumeChange);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', () => setIsLoadingMetadata(false)); // Another place to set loading false
    audio.addEventListener('progress', handleProgress);

    // Set initial volume
    audio.volume = volume;

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('volumechange', handleVolumeChange);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', () => setIsLoadingMetadata(false));
      audio.removeEventListener('progress', handleProgress);
    };
  }, [currentEpisode, volume]); // Re-attach listeners if currentEpisode changes (src changes)

  const handleSeek = useCallback((newTime) => {
    if (audioRef.current && isFinite(newTime)) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, []);

  const handleVolumeChange = useCallback((newVolume) => {
    if (audioRef.current && isFinite(newVolume)) {
      const clampedVolume = Math.max(0, Math.min(1, newVolume)); // Ensure volume is between 0 and 1
      audioRef.current.volume = clampedVolume;
      setVolume(clampedVolume); // Update state to reflect this
    }
  }, []);

  const value = {
    currentEpisode,
    isPlaying,
    duration,
    currentTime,
    volume,
    isLoadingMetadata,
    error, // <--- ADD THIS LINE TO EXPOSE THE ERROR STATE
    bufferedTime, // Expose buffered time
    playEpisode,
    togglePlayPause,
    seek: handleSeek,
    setVolume: handleVolumeChange,
  };
  
  return (
    <AudioContext.Provider value={value}>
      {children}
      {/* Add the actual audio element here, controlled by the context */}
      {/* It's hidden because GlobalPlayerUI will provide custom controls */}
      <audio ref={audioRef} style={{ display: 'none' }} /> 
    </AudioContext.Provider>
  );
};